import { useState } from "react";
import { Check, Palette, Loader2, Sparkles, Zap, Eye, Activity } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { THEMES, DEFAULT_CUSTOM } from "../../themes";

const HEX_FIELDS = [
  { key: "primary", label: "Primary", hint: "Hlavná farba (CTA, akcenty)" },
  { key: "accent", label: "Accent", hint: "Sekundárna (hover, badges)" },
  { key: "bg", label: "Background", hint: "Hlavné pozadie stránky" },
  { key: "surface", label: "Surface", hint: "Karty, panely" },
  { key: "surface_alt", label: "Surface Alt", hint: "Vnorený panel / highlight" },
  { key: "text", label: "Text", hint: "Hlavný text" },
  { key: "text_muted", label: "Text Muted", hint: "Sekundárny text / popisky" },
  { key: "border", label: "Border", hint: "Hranice / oddeľovače" },
];

const AdminThemeTab = () => {
  const { name, custom, setTheme } = useTheme();
  const [draftCustom, setDraftCustom] = useState(custom || DEFAULT_CUSTOM);
  const [saving, setSaving] = useState(false);
  const [customExpanded, setCustomExpanded] = useState(name === "custom");

  const handlePresetSelect = async (key) => {
    setSaving(true);
    try {
      await setTheme(key, null);
    } finally {
      setSaving(false);
    }
  };

  const handleCustomApply = async () => {
    setSaving(true);
    try {
      await setTheme("custom", draftCustom);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-theme-panel" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-cyan-900 rounded-xl p-6 border border-purple-500/30">
        <div className="flex items-center gap-3 mb-2">
          <Palette className="w-6 h-6 text-cyan-300" />
          <h2 className="text-xl font-bold text-white">Dizajn stránky</h2>
        </div>
        <p className="text-purple-200 text-sm">
          Vyber farebnú schému celej aplikácie. Zmena sa aplikuje okamžite vo
          všetkých sekciách (Hero, Showcase, Pricing, CTA…). Môžeš si nastaviť
          aj vlastné hex farby.
        </p>
        <p className="text-xs text-cyan-300 mt-3 font-mono">
          AKTÍVNA TÉMA: {name}
        </p>
      </div>

      {/* 4 preset cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(THEMES).map(([key, theme]) => {
          const isActive = name === key;
          return (
            <div
              key={key}
              data-testid={`theme-card-${key}`}
              className={`bg-white border-2 rounded-xl overflow-hidden transition-all ${
                isActive
                  ? "border-purple-500 ring-2 ring-purple-300 shadow-xl"
                  : "border-gray-200 hover:border-gray-300 hover:shadow-lg"
              }`}
            >
              {/* Visual preview */}
              <div
                className="h-36 relative flex items-center justify-center"
                style={{ background: theme.vars["--color-bg"] }}
              >
                {/* Surface card */}
                <div
                  className="px-5 py-3 rounded-lg border shadow-lg"
                  style={{
                    background: theme.vars["--color-surface"],
                    borderColor: theme.vars["--color-border"],
                  }}
                >
                  <p
                    className="font-bold text-sm"
                    style={{ color: theme.vars["--color-text"] }}
                  >
                    Sample text
                  </p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ background: theme.vars["--color-primary"] }}
                    />
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ background: theme.vars["--color-accent"] }}
                    />
                    <span
                      className="text-[10px] font-mono"
                      style={{ color: theme.vars["--color-text-muted"] }}
                    >
                      {theme.vars["--color-primary"]}
                    </span>
                  </div>
                </div>
                {/* Radial accent glow */}
                <div
                  className="absolute top-3 right-3 w-20 h-20 rounded-full blur-2xl opacity-40"
                  style={{ background: theme.vars["--color-primary"] }}
                />
                {isActive && (
                  <div className="absolute top-3 left-3 bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    AKTÍVNA
                  </div>
                )}
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-gray-900">{theme.label}</h3>
                  <div className="flex gap-1">
                    {theme.preview.map((c) => (
                      <span
                        key={c}
                        className="w-5 h-5 rounded-full border border-gray-200"
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                  {theme.description}
                </p>
                <button
                  onClick={() => handlePresetSelect(key)}
                  disabled={isActive || saving}
                  data-testid={`theme-apply-${key}`}
                  className={`w-full px-3 py-2 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                    isActive
                      ? "bg-purple-100 text-purple-700 cursor-default"
                      : "bg-gray-900 text-white hover:bg-purple-600"
                  }`}
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : isActive ? (
                    <>
                      <Check className="w-3.5 h-3.5" /> Aktívna
                    </>
                  ) : (
                    "Použiť túto tému"
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom hex editor */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
        <button
          data-testid="custom-theme-toggle"
          onClick={() => setCustomExpanded((v) => !v)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-fuchsia-600" />
            <div>
              <h3 className="font-bold text-gray-900">Custom — vlastné hex farby</h3>
              <p className="text-xs text-gray-500">
                Úplná kontrola nad každou farbou (8 premenných)
              </p>
            </div>
          </div>
          <div className="text-gray-400 text-sm">
            {customExpanded ? "▲" : "▼"}
          </div>
        </button>

        {customExpanded && (
          <div className="px-6 pb-6 pt-2 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {HEX_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                    {f.label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={draftCustom[f.key]}
                      onChange={(e) =>
                        setDraftCustom({ ...draftCustom, [f.key]: e.target.value })
                      }
                      data-testid={`custom-color-${f.key}`}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={draftCustom[f.key]}
                      onChange={(e) =>
                        setDraftCustom({ ...draftCustom, [f.key]: e.target.value })
                      }
                      data-testid={`custom-hex-${f.key}`}
                      placeholder="#000000"
                      className="flex-1 px-3 py-2 border border-gray-300 focus:border-purple-500 focus:outline-none rounded-lg text-sm font-mono"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">{f.hint}</p>
                </div>
              ))}
            </div>

            {/* Live preview */}
            <div className="mb-4">
              <p className="text-[10px] text-gray-500 uppercase font-mono tracking-wider mb-2">
                Live preview
              </p>
              <div
                data-testid="custom-preview"
                className="rounded-xl p-6 border"
                style={{
                  background: draftCustom.bg,
                  borderColor: draftCustom.border,
                }}
              >
                <h4
                  className="text-2xl font-black mb-2"
                  style={{ color: draftCustom.text }}
                >
                  Meet PulseAI Engine™
                </h4>
                <p
                  className="text-sm mb-4"
                  style={{ color: draftCustom.text_muted }}
                >
                  Naša proprietárna AI infraštruktúra trénovaná na 2,4 miliardách SEO signálov.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    className="px-4 py-2 rounded-lg text-sm font-bold"
                    style={{ background: draftCustom.primary, color: draftCustom.bg }}
                  >
                    Zobraziť plán
                  </button>
                  <div
                    className="p-3 rounded-lg border"
                    style={{
                      background: draftCustom.surface,
                      borderColor: draftCustom.border,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Zap
                        className="w-4 h-4"
                        style={{ color: draftCustom.primary }}
                      />
                      <span
                        className="text-xs font-bold"
                        style={{ color: draftCustom.text }}
                      >
                        Neural SEO
                      </span>
                    </div>
                  </div>
                  <div
                    className="p-3 rounded-lg border"
                    style={{
                      background: draftCustom.surface_alt,
                      borderColor: draftCustom.border,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Eye
                        className="w-4 h-4"
                        style={{ color: draftCustom.accent }}
                      />
                      <span
                        className="text-xs font-bold"
                        style={{ color: draftCustom.text }}
                      >
                        GEO Layer
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCustomApply}
                disabled={saving}
                data-testid="custom-apply-btn"
                className="flex-1 px-4 py-2.5 bg-gray-900 hover:bg-purple-600 text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Použiť custom farby
              </button>
              <button
                onClick={() => setDraftCustom(DEFAULT_CUSTOM)}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-lg transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="font-semibold mb-1 flex items-center gap-1.5">
          <Activity className="w-4 h-4" />
          Ako to funguje
        </p>
        <ul className="text-xs space-y-1 list-disc list-inside">
          <li>Téma sa aplikuje okamžite — žiadny rebuild</li>
          <li>Uložené v MongoDB, rovnaká téma pre všetkých návštevníkov</li>
          <li>
            Použité v: Hero, PulseAI Showcase, Pricing sekcia, body background
          </li>
          <li>Dashboard & Admin panel zostávajú light-themed pre čitateľnosť</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminThemeTab;
